import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { route } from 'ziggy-js';
import axios from "axios";
import client from "@js/api/client";
export const useSearchStore = defineStore('search', () => {
    const query = ref('');
    const results = ref([]);
    const context = ref('orders');
    const isSearching = ref(false);
    const error = ref(null);
    const pagination = ref({
        meta: null,
        links: null,
    });
    const controllers = ref({});
    const minQueryLength = ref(2);
    const isOrderContext = computed(() => context.value === 'orders');
    const hasMorePages = computed(() => !!pagination.value.links?.next);
    function clearSearch() {
        query.value = '';
        results.value = [];
        pagination.value.meta = null;
        pagination.value.links = null;
        error.value = null;
        isSearching.value = false;
        // Abort any active searches when clearing
        Object.values(controllers.value).forEach(c => c.abort());
        controllers.value = {};
    }
    async function executeSearch(isNew = true) {
        if (isNew && query.value.length < minQueryLength.value)
            return;
        // This removes symbols like ∆, but keeps letters like é, ñ, and ç to prevent 'Illegal mix of collations'
        // errors in legacy SQL databases.
        const sanitizedQuery = query.value.replace(/[^\p{L}\p{N}\s\-_.,]/gu, "").trim();
        if (isNew && sanitizedQuery.length < minQueryLength.value)
            return;
        const currentContext = context.value;
        const routeName = (currentContext === 'locations')
            ? 'api.v1.account.search'
            : 'api.v1.orders.search';
        // Abort the previous request if it's still running
        if (controllers.value[currentContext]) {
            controllers.value[currentContext].abort();
        }
        const controller = new AbortController();
        controllers.value[currentContext] = controller;
        isSearching.value = true;
        // If it's a NEW search, use Ziggy. If it's LOAD MORE, use the 'next' link from Laravel.
        const url = isNew
            ? route(routeName, { q: sanitizedQuery })
            : pagination.value.links?.next;
        if (!url) {
            isSearching.value = false;
            return;
        }
        try {
            const response = await client.get(url, {
                signal: controller.signal
            });
            if (response) {
                // Laravel Resource wraps data in a 'data' key
                results.value = isNew
                    ? response.data
                    : [...results.value, ...response.data];
                pagination.value.meta = response.meta;
                pagination.value.links = response.links;
            }
        }
        catch (e) {
            if (axios.isCancel(e) || e.name === 'AbortError') {
                return;
            }
            error.value = 'Search failed';
            console.error(`Search error:`, e);
        }
        finally {
            if (controllers.value[currentContext] === controller) {
                isSearching.value = false;
            }
        }
    }
    return {
        query,
        results,
        context,
        isSearching,
        error,
        pagination,
        controllers,
        minQueryLength,
        isOrderContext,
        hasMorePages,
        clearSearch,
        executeSearch,
    };
});
